import type { BatchLead } from '../types';
import { CRM_GRID_COPY } from './crmGridCopy';
import { isReady } from './useCrmQueue';
import type { CrmQueueState } from './useCrmQueue';
import type { CrmCounts } from './CrmViews';
import type { FieldChoice, FieldDecision, MatchBucket, PersonMatchState, PersonPreview, PreviewScope } from './types';

/**
 * The unified CRM grid's model (port plan step 6): what each person's status
 * is, which tab they wait on, and what the tab numbers count. Pure functions
 * over the same three inputs the legacy views read (the match check, the live
 * preview and the queue state) plus the lead rows, so `CrmViews` (the counts)
 * and `CrmGrid` (the lenses) can't disagree.
 *
 * Nothing here decides who is SENT. That stays `commitBodyFrom` in
 * `useCrmQueue`, untouched; `isSelectedForSend` below is its own predicate,
 * stated once more so the grid's ticks are exactly the commit set.
 */

/**
 * One status vocabulary for every CRM tab.
 *
 * - `checking`: the live read hasn't landed (it waits for Match or Send, D8).
 * - `unavailable`: the live read failed and there is nothing to show.
 * - `unchecked`: captured after the last match check.
 * - `no_email`: no usable email, so the CRM couldn't be checked first.
 * - `held`: the company's lead rules ("Flag + block send") hold it back.
 * - `ambiguous`: several CRM records share the email. Nothing is changed for
 *   them (ADR 019, merge design G1); merge the duplicates in the CRM, then
 *   Refresh. No "pick the record", no "send anyway" (D3).
 * - `fields_differ`: in the CRM, with conflicting fields nobody has decided.
 * - `in_crm`: in the CRM, nothing left to decide.
 * - `new`: not in the CRM; sending creates a record.
 * - `sent_unchanged`: already sent, nothing new since; collapsed out of the
 *   `'changed'` preview scope (spec 2026-08-30 send-what-changed).
 */
export type CrmKind =
  | 'checking'
  | 'unavailable'
  | 'unchecked'
  | 'no_email'
  | 'held'
  | 'ambiguous'
  | 'fields_differ'
  | 'in_crm'
  | 'new'
  | 'sent_unchanged';

export interface CrmKindInfo {
  kind: CrmKind;
  /** Undecided conflicting fields; > 0 only for `fields_differ`. */
  conflicts: number;
}

/** The status label a kind reads as — the pill's text and the column filter's value. */
export function kindLabel(info: CrmKindInfo): string {
  if (info.kind === 'fields_differ') return CRM_GRID_COPY.kind.fields_differ(info.conflicts);
  return CRM_GRID_COPY.kind[info.kind];
}

/** The order a status column sorts in: work first, settled last. */
export const CRM_KIND_ORDER: Record<CrmKind, number> = {
  fields_differ: 0,
  held: 1,
  no_email: 2,
  ambiguous: 3,
  unavailable: 4,
  unchecked: 5,
  checking: 6,
  in_crm: 7,
  new: 8,
  sent_unchanged: 9,
};

/** The conflict reasons the Details drawer offers a Keep CRM / Use ours pick for. */
const CONFLICT_REASONS = new Set<FieldDecision['reason']>([
  'conflict_default',
  'conflict_choice',
  'policy_overwrite',
]);

/**
 * Conflicting fields on an `existing` person that nobody has picked a side
 * for — the drawer's own `isConflict` rule, minus the fields already picked.
 * Zero under the company's overwrite lock: the drawer's picks are inert
 * there (CRM values are always kept), so there is nothing to decide.
 */
export function unreviewedConflicts(
  p: PersonPreview,
  choices: Record<string, FieldChoice> | undefined,
  overwriteLocked: boolean,
): number {
  if (p.bucket !== 'existing' || overwriteLocked) return 0;
  return p.decisions.filter(
    (d) => d.action !== 'fill' && CONFLICT_REASONS.has(d.reason) && !choices?.[d.field],
  ).length;
}

/**
 * Match and Send's status for one person. The preview is the bucketing
 * authority (it re-reads the CRM, emailless people included), so a person
 * with a preview row is read off it; without one the status says why not.
 */
export function previewKind(args: {
  preview: PersonPreview | undefined;
  match: PersonMatchState | undefined;
  previewLoaded: boolean;
  previewFailed: boolean;
  previewScope: PreviewScope;
  choices: Record<string, FieldChoice> | undefined;
  overwriteLocked: boolean;
}): CrmKindInfo {
  const { preview: p, match, previewLoaded, previewFailed, previewScope } = args;
  if (p) {
    if (p.completeness?.sendBlocked === true) return { kind: 'held', conflicts: 0 };
    if (p.bucket === 'ambiguous') return { kind: 'ambiguous', conflicts: 0 };
    if (p.bucket === 'needs_email') return { kind: 'no_email', conflicts: 0 };
    if (p.bucket === 'new') return { kind: 'new', conflicts: 0 };
    const conflicts = unreviewedConflicts(p, args.choices, args.overwriteLocked);
    return { kind: conflicts > 0 ? 'fields_differ' : 'in_crm', conflicts };
  }
  if (!match) return { kind: 'unchecked', conflicts: 0 };
  if (!previewLoaded) return { kind: previewFailed ? 'unavailable' : 'checking', conflicts: 0 };
  // The read landed without this checked person: under 'changed' that is the
  // backend collapsing an already-sent, unchanged lead.
  return { kind: previewScope === 'changed' ? 'sent_unchanged' : 'unchecked', conflicts: 0 };
}

/** Clean's two reasons a lead is waiting there. Either, both, or neither. */
export function cleanFlags(
  lead: Pick<BatchLead, 'completeness'>,
  match: PersonMatchState | undefined,
): { noEmail: boolean; held: boolean } {
  return {
    noEmail: match?.bucket === 'needs_email',
    // From the LEAD ROW, not the preview (D9): Clean is complete before the
    // live read has run.
    held: lead.completeness?.sendBlocked === true,
  };
}

/**
 * Ticked on Send: exactly `commitBodyFrom`'s filter (ready, not deselected).
 * Already-sent people whose data changed since stay ticked, as today ("send
 * what changed"); only an operator's untick removes anyone.
 */
export function isSelectedForSend(p: PersonPreview, state: Pick<CrmQueueState, 'deselected'>): boolean {
  return isReady(p) && !state.deselected[p.personId];
}

/**
 * The owner worth a second look: `OwnerPicker`'s warning rule (D9 of p3),
 * the badge's owner term and Send's two owner disclosures, all one rule.
 * Ownership never blocks a send; this is only ever reported.
 */
export function ownerNeedsALook(p: PersonPreview): boolean {
  return p.ownerRequired && (p.ownerProvenance === 'integration_account' || p.ownerUnverified === true);
}

export type MatchFilter = 'decide' | 'all' | 'in_crm' | 'new' | 'no_email';

export const MATCH_FILTERS: MatchFilter[] = ['decide', 'all', 'in_crm', 'new', 'no_email'];

/** Whether a Match row shows under `filter`. */
export function inMatchFilter(
  filter: MatchFilter,
  p: PersonPreview,
  info: CrmKindInfo,
  matchBucket: MatchBucket | undefined,
): boolean {
  switch (filter) {
    case 'decide':
      return info.kind === 'fields_differ';
    case 'in_crm':
      // Legacy Match's "Already in your CRM" column: existing + ambiguous.
      return p.bucket === 'existing' || p.bucket === 'ambiguous';
    case 'new':
      return p.bucket === 'new';
    case 'no_email':
      return p.emailChecked === false || p.bucket === 'needs_email' || matchBucket === 'needs_email';
    default:
      return true;
  }
}

/** Grid-mode tab numbers plus what the badge's sentence may claim. */
export type CrmGridCounts = CrmCounts & { badgeIncludesLiveRead: boolean };

/**
 * The tab numbers in grid mode (port plan D9) — "work waiting":
 *
 * - Clean: no email (match check) or held by lead rules (lead rows), each
 *   person once. `null` until both are known.
 * - Match: people with conflicting fields nobody has decided. `null` until
 *   the live read has landed.
 * - Send: people ticked to send (= the commit set). `null` until the live
 *   read has landed.
 * - badge: no-email ∪ held ∪ needs-a-decision ∪ unresolved owner, de-duplicated
 *   by person, counting only the terms already known.
 */
export function crmGridCounts(args: {
  /** Match-check rows; `undefined` until the check lands. */
  results: PersonMatchState[] | undefined;
  /** The event's active lead rows; `undefined` until they load. */
  leadRows: Array<Pick<BatchLead, 'id' | 'completeness'>> | undefined;
  previews: PersonPreview[];
  previewLoaded: boolean;
  state: Pick<CrmQueueState, 'deselected' | 'fieldChoices'>;
  overwriteLocked: boolean;
}): CrmGridCounts {
  const { results, leadRows, previews, previewLoaded, state, overwriteLocked } = args;
  const noEmail = new Set((results ?? []).filter((r) => r.bucket === 'needs_email').map((r) => r.personId));
  const held = new Set(
    (leadRows ?? []).filter((l) => l.completeness?.sendBlocked === true).map((l) => l.id),
  );
  const clean = results !== undefined && leadRows !== undefined ? new Set([...noEmail, ...held]).size : null;

  const decide = previewLoaded
    ? previews
        .filter((p) => unreviewedConflicts(p, state.fieldChoices[p.personId], overwriteLocked) > 0)
        .map((p) => p.personId)
    : [];
  const owner = previewLoaded ? previews.filter(ownerNeedsALook).map((p) => p.personId) : [];

  return {
    clean,
    match: previewLoaded ? decide.length : null,
    send: previewLoaded ? previews.filter((p) => isSelectedForSend(p, state)).length : null,
    badge: new Set([...noEmail, ...held, ...decide, ...owner]).size,
    badgeIncludesLiveRead: previewLoaded,
  };
}
